// src/app/services/auth/auth.service.ts (CMS)

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, from, map, Observable, of, switchMap, tap } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ILogin } from '../../interfaces/ILogin';
import { createCmsJwt } from '../../utils/jwt-app.utils';

export interface IAppLoginApiResponse {
  status: 'SUCCESS' | 'ERROR' | string;
  message: string;
  content?: {
    id?: string;
    name?: string;
    email?: string;
    role?: string;
    role_label?: string;
    token?: string;
    refresh_token?: string;
    csrf_token?: string;
    companies?: { id: string; legal_name: string }[];
    fuel_stations?: { id: string; name: string }[];
    [key: string]: any;
  };
  type?: string;
  code?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<IAppLoginApiResponse['content'] | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.currentUser$.pipe(map(user => !!user));
  private token: string | null = null;
  private refreshTokenVal: string | null = null;
  private csrfToken: string | null = null;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  private storeSession(content?: IAppLoginApiResponse['content']): void {
    if (!content) return;

    if (content.token) {
      this.token = content.token;
      try {
        sessionStorage.setItem('cms_token', content.token);
      } catch (e) {}
    }

    if (content.refresh_token) {
      this.refreshTokenVal = content.refresh_token;
      try {
        sessionStorage.setItem('cms_refresh_token', content.refresh_token);
      } catch (e) {}
    }

    if (content.csrf_token) {
      this.csrfToken = content.csrf_token;
      try {
        sessionStorage.setItem('cms_csrf_token', content.csrf_token);
      } catch (e) {}
    }
  }

  login(credentials: ILogin): Observable<IAppLoginApiResponse> {
    return this.loginCms(credentials);
  }

  private createCmsLoginJwt(credentials: ILogin): Promise<string> {
    const payload = {
      email: credentials.username,
      password: credentials.password,
    };

    const secret = environment.jwtSecretKeyCms;

    return createCmsJwt(payload, secret);
  }

  private loginCms(credentials: ILogin): Observable<IAppLoginApiResponse> {
    const url = `${environment.apiUrl}/public/cms/authentication/login`;

    return from(this.createCmsLoginJwt(credentials)).pipe(
      switchMap((jwt: string) => {
        const body = { payload: jwt };
        console.log('[AuthService CMS] body enviado ao login:', body);
        return this.http.post<IAppLoginApiResponse>(url, body, { withCredentials: true });
      }),
      tap((response: IAppLoginApiResponse) => {
        console.log('[AuthService CMS] resposta de login:', response);
        if (response && response.status === 'SUCCESS' && response.content) {
          this.storeSession(response.content);
          this.currentUserSubject.next(response.content);
        }
      })
    );
  }

  checkSession(): Observable<IAppLoginApiResponse> {
    const url = `${environment.apiUrl}/private/cms/authentication/me`;
    return this.http.get<IAppLoginApiResponse>(url, { withCredentials: true }).pipe(
      tap((response: IAppLoginApiResponse) => {
        if (response && response.status === 'SUCCESS' && response.content) {
          this.storeSession(response.content);
          this.currentUserSubject.next(response.content);
        } else {
          this.currentUserSubject.next(null);
        }
      }),
      catchError((err) => {
        console.warn('[AuthService CMS] Sessão expirada ou inválida:', err);
        this.currentUserSubject.next(null);
        return of({ status: 'ERROR', message: 'Sessão inválida' } as IAppLoginApiResponse);
      })
    );
  }

  refreshToken(): Observable<IAppLoginApiResponse> {
    const url = `${environment.apiUrl}/public/cms/authentication/refresh`;
    const body: any = {};
    const rt = this.getRefreshToken();
    if (rt) {
      body.refresh_token = rt;
    }
    return this.http.post<IAppLoginApiResponse>(url, body, { withCredentials: true }).pipe(
      tap((response: IAppLoginApiResponse) => {
        if (response && response.status === 'SUCCESS' && response.content) {
          this.storeSession(response.content);
          this.currentUserSubject.next(response.content);
        }
      }),
      catchError((err) => {
        console.error('[AuthService CMS] Erro no refresh token:', err);
        this.currentUserSubject.next(null);
        return of({ status: 'ERROR', message: 'Refresh falhou' } as IAppLoginApiResponse);
      })
    );
  }

  getCurrentUser(): IAppLoginApiResponse['content'] | null {
    return this.currentUserSubject.value;
  }

  getUserRole(): 'super_admin' | 'workshop_owner' | 'fuel_station_owner' | 'tow_truck_owner' | string | null {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  isSuperAdmin(): boolean {
    return this.getUserRole() === 'super_admin';
  }

  isWorkshopOwner(): boolean {
    return this.getUserRole() === 'workshop_owner';
  }

  isFuelStationOwner(): boolean {
    return this.getUserRole() === 'fuel_station_owner';
  }

  isTowTruckOwner(): boolean {
    return this.getUserRole() === 'tow_truck_owner';
  }

  getToken(): string | null {
    if (this.token) return this.token;
    try {
      const stored = sessionStorage.getItem('cms_token');
      if (stored) {
        this.token = stored;
        return stored;
      }
    } catch (e) {}
    return null;
  }

  getCmsToken(): string | null {
    return this.getToken();
  }

  getRefreshToken(): string | null {
    if (this.refreshTokenVal) return this.refreshTokenVal;
    try {
      const stored = sessionStorage.getItem('cms_refresh_token');
      if (stored) {
        this.refreshTokenVal = stored;
        return stored;
      }
    } catch (e) {}
    return null;
  }

  isLoggedIn(): boolean {
    return !!this.currentUserSubject.value;
  }

  logout(): void {
    this.token = null;
    this.refreshTokenVal = null;
    this.csrfToken = null;
    try {
      sessionStorage.removeItem('cms_token');
      sessionStorage.removeItem('cms_refresh_token');
      sessionStorage.removeItem('cms_csrf_token');
    } catch (e) {}
    const url = `${environment.apiUrl}/public/cms/authentication/logout`;
    this.http.post(url, {}, { withCredentials: true }).subscribe({
      next: () => {
        this.currentUserSubject.next(null);
        this.router.navigate(['/login']);
      },
      error: (err) => {
        console.error('[AuthService] Erro ao fazer logout:', err);
        this.currentUserSubject.next(null);
        this.router.navigate(['/login']);
      }
    });
  }

  getCsrfToken(): string | null {
    if (this.csrfToken) {
      return this.csrfToken;
    }
    try {
      const stored = sessionStorage.getItem('cms_csrf_token');
      if (stored) {
        this.csrfToken = stored;
        return stored;
      }
    } catch (e) {}

    const matches = document.cookie.match(new RegExp('(?:^|; )csrf_token=([^;]*)'));
    return matches ? decodeURIComponent(matches[1]) : null;
  }
}
